import { useTranslation } from 'react-i18next';

import { Font, StyleSheet, View } from '@react-pdf/renderer';
import { Chart } from 'chart.js';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { ChartPdfImagesGroup } from 'PDF/components/Charts/ChartPdfImagesGroup';
import { IImageChart } from 'PDF/components/Charts/types';
import { FinBlockTable, IFinBlockTableProps } from 'PDF/components/FinBlockTable';
import { List } from 'PDF/components/List/List';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import { shouldRenderFinancialData } from 'PDF/helpers/shouldRenderFinancialData';
import {
  FinancialPlanKeys,
  FinancialPlanSectionAvailability,
  getFinancialPlanChapterNames,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { IOpeningBalanceSheets } from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';

import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);
Chart.defaults.font.family = 'Noto Sans';

interface IFinancialPlanData {
  plannedExpensesTable: IFinBlockTableProps;
  plannedExpensesChartImage: IImageChart;
  costPerUnitChartImage: IImageChart;
  variableExpensesTable: IFinBlockTableProps;
  variableExpensesChartImage: IImageChart;
  donutChartsImages: IImageChart[];
  personnelExpenses: IFinBlockTableProps;
  sourcesOfProjectFinancingAndloanRepaymentChartImages: IImageChart[];
  profitabilityRatiosChartImage: IImageChart;
  lineChartsImages: IImageChart[];
  solvencyRatiosChartImage: IImageChart;
  projectedBalanceSheetAssetsChart: IImageChart | null;
  projectedBalanceSheetEquityAndLiabilitiesChart: IImageChart | null;
  projectedBalanceSheet: IFinBlockTableProps;
  fundingDirectionsTableData: IFinBlockTableProps;
  fundingDirectionsChartImage: IImageChart;
  investmentPerformanceRatios: IFinBlockTableProps;
  otherPerformanceRatios: IFinBlockTableProps;
  projectedCashFlowTable: IFinBlockTableProps;
  projectedCashFlowChartImage: IImageChart;
  profitAndLossTableData: IFinBlockTableProps;
  profitAndLossChart: IImageChart;
}

export interface IFinancialPlanPdfProps {
  data: IFinancialPlanData;
  magazineItems: ITextItem[][];
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const FinancialPlanPdf = (
  props: IFinancialPlanPdfProps & {
    availability: FinancialPlanSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
    openingBalanceSheetData?: IOpeningBalanceSheets;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    openingBalanceSheetData,
    isStandardOrLightTariff,
    isExamplePage,
  } = props;

  const { t } = useTranslation();

  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.FINANCIAL_PLAN, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const FINANCIAL_PLAN_CHAPTER_NAMES = getFinancialPlanChapterNames(isExamplePage);
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const shouldRenderProjectedBalanceSheetByOpeningBalance =
    !!openingBalanceSheetData && shouldRenderFinancialData(openingBalanceSheetData);

  const subChapterNames = (Object.keys(availability) as FinancialPlanKeys[])
    .filter((key) => {
      if (key === 'projectedBalanceSheet' && openingBalanceSheetData) {
        return shouldRenderFinancialData(openingBalanceSheetData) && !!availability[key];
      } else {
        return !!availability[key];
      }
    })
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${FINANCIAL_PLAN_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.FINANCIAL_PLAN)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 1, variant: 'after' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const {
    plannedExpensesTable,
    plannedExpensesChartImage,
    costPerUnitChartImage,
    variableExpensesTable,
    variableExpensesChartImage,
    donutChartsImages,
    sourcesOfProjectFinancingAndloanRepaymentChartImages,
    profitabilityRatiosChartImage,
    lineChartsImages,
    solvencyRatiosChartImage,
    personnelExpenses,
    projectedBalanceSheetAssetsChart,
    projectedBalanceSheetEquityAndLiabilitiesChart,
    projectedBalanceSheet,
    fundingDirectionsTableData,
    fundingDirectionsChartImage,
    profitAndLossTableData,
    profitAndLossChart,
    investmentPerformanceRatios,
    otherPerformanceRatios,
    projectedCashFlowTable,
    projectedCashFlowChartImage,
  } = data;

  const financialPerformanceImagesForBank = () => {
    if (openingBalanceSheetData && shouldRenderFinancialData(openingBalanceSheetData)) {
      return [...lineChartsImages.slice(1), solvencyRatiosChartImage];
    }
  };
  const financialPerformanceImages = () => {
    return [profitabilityRatiosChartImage, lineChartsImages[0]];
  };
  const bankFinancialPerformanceImages = financialPerformanceImagesForBank();

  const addViewWithMarginBottom =
    magazineItems[0].some((el) => Boolean(el.text)) || magazineItems[1].some((el) => Boolean(el.text));

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.FINANCIAL_PLAN)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 1, variant: 'after' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.keyAssumption && (
            <>
              <SubChapterHeading
                id="keyAssumption"
                heading={getSubHeading(FINANCIAL_PLAN_CHAPTER_NAMES.keyAssumption)}
              />
              {magazineItems[0].some((el) => Boolean(el.text)) && (
                <SmallHeading
                  heading={isExamplePage ? 'Sales Assumptions' : t('pdfPages.mappings.financialPlan.salesAssumptions')}
                />
              )}
              <List
                showBullets
                withBottomMargin={magazineItems[1].some((el) => Boolean(el.text))}
                steps={magazineItems[0].filter((el) => el.text).map((el) => el.text)}
              />
              {magazineItems[1].some((el) => Boolean(el.text)) && (
                <SmallHeading
                  heading={
                    isExamplePage ? 'Expenses Assumptions' : t('pdfPages.mappings.financialPlan.expensesAssumptions')
                  }
                />
              )}
              <List showBullets steps={magazineItems[1].filter((el) => el.text).map((el) => el.text)} />
            </>
          )}
          {addViewWithMarginBottom && <View style={{ marginBottom: '30px' }}></View>}
          {availability.profitLossProjection && (
            <>
              <SubChapterHeading
                headingStyles={styles.subChapterHeadingBeforeTable}
                id="profitLossProjection"
                heading={getSubHeading(FINANCIAL_PLAN_CHAPTER_NAMES.profitLossProjection)}
                minPresenceAhead={200}
              />
              <FinBlockTable isAlwaysNumberFloat {...profitAndLossTableData} />
              <ChartPdfImagesGroup images={[profitAndLossChart]} />
              <FinBlockTable
                heading={isExamplePage ? 'Planned Expenses' : t('financialPlan.plannedExpenses.title')}
                noBorderBeforeTotal
                isAlwaysNumberFloat
                {...plannedExpensesTable}
                headingStyles={{ marginBottom: 17 }}
              />
              <ChartPdfImagesGroup images={[plannedExpensesChartImage, costPerUnitChartImage]} />
              <View minPresenceAhead={200} />
              <FinBlockTable
                heading={
                  isExamplePage
                    ? 'The Cost of Each Unit or Group of Products/Services (Variable Expenses)'
                    : t('pdfPages.mappings.financialPlan.costOfEachUnitOrGroup')
                }
                noBorderBeforeTotal
                headingStyles={{ marginBottom: 8 }}
                {...variableExpensesTable}
              />
              <ChartPdfImagesGroup images={[variableExpensesChartImage]} />
              <ChartPdfImagesGroup images={donutChartsImages} headingProps={{ withBottomMargin: false }} />
              <View wrap={false}>
                <FinBlockTable
                  heading={
                    isExamplePage ? 'The Personnel Expenses' : t('pdfPages.mappings.financialPlan.personnelExpenses')
                  }
                  noBorderBeforeTotal
                  headingStyles={{ marginBottom: 8 }}
                  {...personnelExpenses}
                />
              </View>
            </>
          )}
          {availability.projectedCashFlow && (
            <>
              <SubChapterHeading
                headingStyles={styles.subChapterHeadingBeforeTable}
                id="projectedCashFlow"
                heading={getSubHeading(FINANCIAL_PLAN_CHAPTER_NAMES.projectedCashFlow)}
              />
              <FinBlockTable {...projectedCashFlowTable} isAlwaysNumberFloat />
              <ChartPdfImagesGroup images={[projectedCashFlowChartImage]} />
              <PdfContentText items={magazineItems[2]} />
              <ChartPdfImagesGroup images={sourcesOfProjectFinancingAndloanRepaymentChartImages} />
              <FinBlockTable
                heading={
                  isExamplePage
                    ? "The Project's Funding Directions Over Time"
                    : t('pdfPages.mappings.financialPlan.projectFindingDirections')
                }
                noBorderBeforeTotal
                {...fundingDirectionsTableData}
                headingStyles={{ marginBottom: 16 }}
              />
              <ChartPdfImagesGroup images={[fundingDirectionsChartImage]} {...fundingDirectionsTableData} />
            </>
          )}
          {availability.projectedBalanceSheet &&
            openingBalanceSheetData &&
            shouldRenderProjectedBalanceSheetByOpeningBalance && (
              <>
                <SubChapterHeading
                  id="projectedBalanceSheet"
                  headingStyles={styles.subChapterHeadingBeforeTable}
                  heading={getSubHeading(FINANCIAL_PLAN_CHAPTER_NAMES.projectedBalanceSheet)}
                />
                <FinBlockTable isAlwaysNumberFloat {...projectedBalanceSheet} />
                {!!projectedBalanceSheetAssetsChart && (
                  <ChartPdfImagesGroup images={[projectedBalanceSheetAssetsChart]} />
                )}
                {!!projectedBalanceSheetEquityAndLiabilitiesChart && (
                  <ChartPdfImagesGroup images={[projectedBalanceSheetEquityAndLiabilitiesChart]} />
                )}
                <PdfContentText items={magazineItems[3]} />
              </>
            )}
          {availability.financialPerformance && (
            <>
              <View wrap={false}>
                <SubChapterHeading
                  id="financialPerformance"
                  heading={getSubHeading(FINANCIAL_PLAN_CHAPTER_NAMES.financialPerformance)}
                />
                <FinBlockTable
                  {...investmentPerformanceRatios}
                  heading={
                    isExamplePage
                      ? 'Investment Performance Ratios'
                      : t('pdfPages.mappings.financialPlan.investmentPerformanceRatios')
                  }
                  hasLeftHeading
                  isRatiosTable
                  firstColumnWidth={252}
                />
              </View>
              <PdfContentText items={magazineItems[!isInvestor ? 4 : 3]} />
              <FinBlockTable {...otherPerformanceRatios} hasLeftHeading isRatiosTable />
              <ChartPdfImagesGroup images={financialPerformanceImages()} />
              {!isInvestor && !!bankFinancialPerformanceImages?.length && (
                <>
                  <ChartPdfImagesGroup images={bankFinancialPerformanceImages} />
                </>
              )}

              <PdfContentText items={magazineItems[!isInvestor ? 5 : 4]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  subChapterHeadingBeforeTable: {
    marginBottom: 17,
  },
});
